import DisposalType from 'owa-service/lib/contract/DisposalType';
import distinguishedFolderId from 'owa-service/lib/factory/distinguishedFolderId';
import emptyFolderOperation from 'owa-service/lib/operation/emptyFolderOperation';
import emptyFolderRequest from 'owa-service/lib/factory/emptyFolderRequest';
import Folder from 'owa-service/lib/contract/Folder';
import folderId from 'owa-service/lib/factory/folderId';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isSharedFolder from 'owa-mail-store/lib/utils/isSharedFolder';
import itemId from 'owa-service/lib/factory/itemId';
import SingleResponseMessage from 'owa-service/lib/contract/SingleResponseMessage';
import { BulkActionCustomDataBase } from 'owa-bulk-action-store';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';

/**
 * Create and submit the empty folder action for the specified folder
 * @param folder the folder to be emptied
 * @param disposalType the disposal type
 * @param deleteSubFolder whether to delete subfolder
 * @param clientLastSyncTime the client last sync time watermark
 * @param itemIdsToExclude item ids to exclude from empty folder operation
 * @param shouldSupportBulkActions flag indicating whether the bulk actions is supported for this empty folder request
 */
export function emptyFolderService(
    folder: Folder,
    disposalType: DisposalType,
    deleteSubFolder: boolean,
    clientLastSyncTime: Date,
    itemIdsToExclude: string[],
    shouldSupportBulkActions: boolean,
    bulkActionCustomData?: BulkActionCustomDataBase
): Promise<SingleResponseMessage> {
    const requestBody = configureRequestBody(
        folder,
        disposalType,
        deleteSubFolder,
        clientLastSyncTime,
        itemIdsToExclude,
        shouldSupportBulkActions,
        bulkActionCustomData
    );
    return emptyFolderOperation({
        Header: getJsonRequestHeader(),
        Body: requestBody,
    }).then(response => {
        return response.Body.ResponseMessages.Items[0];
    });
}

/**
 * Configure the empty folder request body
 * @param folder the folder to be emptied
 * @param disposalType the disposal type
 * @param deleteSubFolder whether to delete subfolder
 * @param clientLastSyncTime the client last sync time watermark
 * @param itemIdsToExclude item ids to exclude from empty folder operation
 * @param shouldSupportBulkActions flag indicating whether the bulk actions is supported for this empty folder request
 * @param bulkActionCustomData custom data to be sent down to BulkActionItem
 */
function configureRequestBody(
    folder: Folder,
    disposalType: DisposalType,
    deleteSubFolder: boolean,
    clientLastSyncTime: Date,
    itemIdsToExclude: string[],
    shouldSupportBulkActions: boolean,
    bulkActionCustomData?: BulkActionCustomDataBase
) {
    let folderIdToEmpty = null;

    const distinguishedFolderIdName = folderIdToName(folder.FolderId.Id);
    folderIdToEmpty =
        distinguishedFolderIdName == 'none'
            ? folderId({ Id: folder.FolderId.Id })
            : distinguishedFolderId({ Id: distinguishedFolderIdName });
    const baseItemIds =
        itemIdsToExclude != undefined ? itemIdsToExclude.map(id => itemId({ Id: id })) : undefined;

    const body = emptyFolderRequest({
        FolderIds: [folderIdToEmpty],
        AllowSearchFolder: true,
        DeleteType: disposalType,
        DeleteSubFolders: deleteSubFolder,
        SuppressReadReceipt:
            getUserConfiguration().UserOptions.ReadReceipt != 'AlwaysSend' ||
            isSharedFolder(folder.FolderId.Id),
        ItemIdsToExclude: baseItemIds,
        BulkActionRequest: shouldSupportBulkActions,
        BulkActionCustomData: bulkActionCustomData && JSON.stringify(bulkActionCustomData),
        BulkActionBatchSize: 10,
    });

    // Server does not support specify ClientLastSyncTime and deleting subfolders at the same time
    if (deleteSubFolder && clientLastSyncTime) {
        throw new Error('ClientLastSyncTime is not supported when deleting subfolders.');
    }

    // Specify ClientLastSyncTime if not delete sub folders
    if (!deleteSubFolder) {
        body.ClientLastSyncTime = clientLastSyncTime && clientLastSyncTime.toISOString();
    }

    return body;
}
